import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useMemo } from 'react';
import { SEBS_COMPOSER_EXPERIMENT } from '~/constants/composer';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/vdp/experiments';
import { useUserIsImpersonating } from '~/context/common/UserContext';
import {
  useComposerAssignedBucket,
  useShowComposerTreatment
} from '~/context/composer/ComposerContext';
import {
  useDealershipId,
  useIsDealershipPage
} from '~/context/dealership/StaticContext';
import { useVehicleAvailability } from '~/context/vdp/UserVehicleContext';
import {
  useGetVehicleDetails,
  useIsNewVehicle
} from '~/context/vdp/VehicleContext';
import useEventExperiments from '~/hooks/common/telemetry/useEventExperiments';
import useSendFeatureBranchToDataDog from '~/hooks/common/telemetry/useSendFeatureBranchToDataDog';
import useSendNativeDeviceTypeToDataDog from '~/hooks/common/telemetry/useSendNativeDeviceTypeToDataDog';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useTrackDeliveryPickupVisibility from '~/hooks/vdp/telemetry/useTrackDeliveryPickupVisibility';
import useTrackMiuInspectionRendered from '~/hooks/vdp/telemetry/useTrackMiuInspectionRendered';
import useTrackWhyBuyImageryRendered from '~/hooks/vdp/telemetry/useTrackWhyBuyImageryRendered';
import useSendSptEvent from '~/hooks/vdp/useSendSptEventAndActions';
import useVaultDataEvent from '~/hooks/vdp/useVaultDataEvent';
import useWhyBuyDataEvent from '~/hooks/vdp/useWhyBuyDataEvent';
import { BrowserCookies } from '~/utilities';
import { isSpaNavigation } from '~/utilities/navigationTiming';
import {
  useSendAvailabilityData,
  useSendDeliveryMiles,
  useSendDeliveryPickup,
  useSendPriceDropEvent,
  useSendSoftPull,
  useSendVehiclesAvailableSooner,
  useSendZipCodeChanges
} from './events';

export default function useSendDataEvents() {
  const vehicleAvailability = useVehicleAvailability();
  const {
    isSold,
    notAvailableInCA,
    otherUnavailable,
    outOfMarket,
    purchasePending,
    stopSaleRecall,
    unavailable
  } = useAlertStatus();
  const { inventoryType, stores, vehicleId } = useGetVehicleDetails();
  const composerAssignedBucket = useComposerAssignedBucket();
  const showComposerTreatment = useShowComposerTreatment();
  const isImpersonating = useUserIsImpersonating();
  const isNewCar = useIsNewVehicle();
  const dealershipId = useDealershipId();
  const isDealershipPage = useIsDealershipPage();

  useEffect(() => {
    datadogRum.setGlobalContextProperty('isSpaNav', isSpaNavigation());
  }, []);

  useEffect(() => {
    datadogRum.setGlobalContextProperty('inventoryType', inventoryType);
    datadogRum.setGlobalContextProperty('isImpersonating', isImpersonating);
    datadogRum.setGlobalContextProperty('stores', stores);
    datadogRum.setGlobalContextProperty('Geolocation', {
      CVCurrentState: BrowserCookies.get('CVCurrentState'),
      CVCurrentZip: BrowserCookies.get('CVCurrentZip')
    });
    datadogRum.setGlobalContextProperty('dealershipId', dealershipId);
    datadogRum.setGlobalContextProperty('isDealershipPage', isDealershipPage);
    datadogRum.setGlobalContextProperty(
      'showComposerTreatment',
      showComposerTreatment
    );
  }, [
    dealershipId,
    isDealershipPage,
    inventoryType,
    isImpersonating,
    showComposerTreatment,
    stores
  ]);

  const composerExperiments = useMemo(
    () => ({ [SEBS_COMPOSER_EXPERIMENT]: composerAssignedBucket }),
    [composerAssignedBucket]
  );

  useEventExperiments(
    browserCookieIdExperiments,
    userIdExperiments,
    composerExperiments
  );
  useSendNativeDeviceTypeToDataDog();
  useSendAvailabilityData();
  useSendDeliveryMiles();
  useSendDeliveryPickup();
  useSendFeatureBranchToDataDog();
  useSendSoftPull();
  useSendSptEvent();
  useSendVehiclesAvailableSooner();
  useTrackDeliveryPickupVisibility();
  useTrackMiuInspectionRendered();
  useTrackWhyBuyImageryRendered();
  useVaultDataEvent();
  useWhyBuyDataEvent();
  useSendZipCodeChanges();
  useSendPriceDropEvent();

  useEffect(() => {
    const alerts = [
      { type: isSold, description: 'vehicle-is-sold' },
      { type: notAvailableInCA, description: 'vehicle-not-available-in-ca' },
      { type: otherUnavailable, description: 'vehicle-other-unavailable' },
      { type: outOfMarket, description: 'vehicle-out-of-market' },
      { type: purchasePending, description: 'vehicle-purchase-pending' },
      { type: stopSaleRecall, description: 'vehicle-stop-sale-recall' },
      { type: unavailable, description: 'vehicle-unavailable' }
    ];

    alerts.forEach(alert => {
      if (alert.type) {
        datadogRum.addAction(`vdp-alert-${alert.description}`, { vehicleId });
      }
    });
  }, [
    isSold,
    notAvailableInCA,
    otherUnavailable,
    outOfMarket,
    purchasePending,
    stopSaleRecall,
    unavailable,
    vehicleId
  ]);

  useEffect(() => {
    if (otherUnavailable) {
      datadogRum.addAction(
        'unknown vehicle availability error',
        vehicleAvailability
      );
    }
  }, [otherUnavailable, vehicleAvailability]);

  useEffect(() => {
    datadogRum.setGlobalContextProperty('isNewCar', isNewCar);
  }, [isNewCar]);

  useEffect(() => {
    datadogRum.setGlobalContextProperty('isSold', isSold);
  }, [isSold]);
}
